/**
 * MmsSidePanel Component
 * 
 * Main container for MMS Mode side panel.
 * Includes model search, action buttons, and model tree.
 */

import React, { useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input } from '@hydrodigital/design-system';
import { PlayCircleOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import DashboardContext from '@/contexts/DashboardContext';
import { MmsModelTree } from './MmsModelTree';
import type { ModelVersion } from './MmsModelTree';
import { CreateModelModal } from './CreateModelModal';
import type { UseMmsMapDataResult } from '@/pages/Dashboard/hooks/useMmsMapData';
import HydraulicSimulatorService from '@/domain/HydraulicSimulator/HydraulicSimulatorService';
import { notification } from 'antd';

const hydraulicSimulatorService = new HydraulicSimulatorService();

export interface MmsSidePanelProps {
	onClose?: () => void;
	mmsMapData: UseMmsMapDataResult;
}

export const MmsSidePanel: React.FC<MmsSidePanelProps> = ({ onClose, mmsMapData }) => {
	const { value: context, setValue: setContext } = useContext(DashboardContext);
	const navigate = useNavigate();
	const [searchTerm, setSearchTerm] = useState('');
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [treeRefreshKey, setTreeRefreshKey] = useState(0);
	const [treeExpandModelId, setTreeExpandModelId] = useState<string | undefined>(undefined);


	const handleModelSelect = useCallback((modelId: string, fileName: string) => {
		setContext((prev) => ({
			...prev,
			selectedMmsId: modelId,
			selectedMmsFileName: fileName,
		}));
	}, [setContext]);

	const handleSaveChanges = async () => {
		if (!context.selectedMmsId) {
			notification.warning({
				message: 'No Model Selected',
				description: 'Please select a model before saving changes.',
			});
			return;
		}

		try {
			await mmsMapData.saveChanges();
		} catch (error) {
			console.error('Error saving changes:', error);
		}
	};

	const handleCreateModel = () => {
		setIsCreateModalOpen(true);
	};

	const handleRunSelectedModel = async () => {
		if (!context.selectedMmsId) {
			notification.warning({
				message: 'No Model Selected',
				description: 'Please select a model version before running.',
			});
			return;
		}
		notification.info({
			message: 'Running Model',
			description: `Running model: ${context.selectedMmsFileName || context.selectedMmsId}`,
		});
	};

	const handleSaveAsChild = useCallback(async (file: ModelVersion) => {
		if (!mmsMapData.assetsData) {
			notification.warning({
				message: 'No Data',
				description: 'Load the model before saving changes.',
			});
			return;
		}

		const dataToSend = mmsMapData.assetsData.assets
			?.filter((asset: any) => asset.invisible || asset.edited)
			.map((asset: any) => {
				const coordinates =
					asset.coordinates?.[0]?.[1] && asset.coordinates?.[0]?.[0] && asset.type !== 'conduits'
						? [
								{ type: 'coordinates', name: 'x', value: asset.coordinates[0][0].toString() },
								{ type: 'coordinates', name: 'y', value: asset.coordinates[0][1].toString() },
						  ]
						: [];
				return {
					assetId: asset.assetId,
					action: asset.edited ? 'UPDATE' : asset.invisible ? 'DELETE' : '',
					type: asset.type,
					attributeDTOList: [
						...coordinates,
						...(asset.attributes
							? asset.attributes
									.filter((at: any) => at.name !== 'name')
									.map((at: any) => ({
										name: at.name,
										value: at.value?.toString() ?? '',
									}))
							: []),
					],
				};
			});

		if (!dataToSend || dataToSend.length === 0) {
			notification.warning({
				message: 'No Changes',
				description: 'No modifications found to save as child model.',
			});
			return;
		}

		try {
			const response = await hydraulicSimulatorService.postSaveChangesModelFile(
				file.model_id,
				dataToSend,
				Number(context.selectedLocation),
			);

			if (response) {
				notification.success({
					message: 'Saved',
					description: `New child version created: ${String(response).substring(0, 8)}`,
				});
				setTreeExpandModelId(file.model_id);
				setTreeRefreshKey((prev) => prev + 1);
				handleModelSelect(String(response), file.alias);
			}
		} catch (error) {
			console.error('Error saving as child model:', error);
			notification.error({
				message: 'Error',
				description: 'Failed to save as child model.',
			});
		}
	}, [mmsMapData.assetsData, context.selectedLocation, handleModelSelect]);

	const handleExportToFiles = useCallback((file: ModelVersion) => {
		navigate(`/upload/modelfiles?mmsId=${file.model_id}&fileName=${file.alias}`);
	}, [navigate]);

	const hasChanges = mmsMapData.assetsData?.assets?.some((asset: any) => asset.edited) || false;

	return (
		<div className="mms-side-panel flex flex-col h-full">
			{/* Primary Action Buttons */}
			<div className="px-5 pb-4 flex flex-col gap-3">
				{/* <Button
					iconName='play-circle'
					onClick={handleRunSelectedModel}
					disabled={!context.selectedMmsId}
					variant="primary"
					style={{ width: '100%', fontWeight: '600', borderRadius: '10px', height: '44px', fontSize: '14px' }}
					label='Run Selected Model'
				/> */}
				<Button
					iconName='plus-circle'
					onClick={handleCreateModel}
					size='small'
					variant="secondary"
					className="w-full font-semibold rounded-[10px] h-10 text-sm"
					label='Create New Model'
				/>
			</div>

			{/* Save Changes (only when there are pending edits) */}
			{/* {hasChanges && (
				<div className="px-5 pb-3">
					<Button
						onClick={handleSaveChanges}
						disabled={mmsMapData.isLoading}
						variant="primary"
						className="w-full rounded-[10px] h-9"
						label={mmsMapData.isLoading ? 'Saving...' : 'Save Changes'}
						iconName='save'
					/>
				</div>
			)} */}

			{/* Available Models section */}
			<div className="px-5 pb-2">
				<h4 className="text-sm font-bold text-[#1a1a2e] m-0 mb-2.5">Available Models</h4>
				<Input
					placeholder="Search by Model"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					className="w-full rounded-full"
					size='small'
				/>
			</div>

			{/* Model Tree */}
			<div className="flex-1 overflow-auto">
				{context.selectedLocation ? (
					<MmsModelTree
						onModelSelect={handleModelSelect}
						selectedModelId={context.selectedMmsId}
						searchTerm={searchTerm}
						onSaveAsChild={handleSaveAsChild}
						onExportToFiles={handleExportToFiles}
						refreshKey={treeRefreshKey}
						expandModelId={treeExpandModelId}
					/>
				) : (
					<div className="flex items-center justify-center h-full p-4">
						<p className="text-sm text-gray-500 text-center">
							Please select a location to view models
						</p>
					</div>
				)}
			</div>

			{/* Footer: nodes/lines info */}
			{(mmsMapData.allNodes.length > 0 || mmsMapData.isLoading) && (
				<div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
					<p className="text-xs text-gray-500">
						{mmsMapData.isLoading ? (
							<span className="text-blue-600">Loading model data...</span>
						) : (
							<><span className="font-semibold">{mmsMapData.allNodes.length}</span> nodes,{' '}
							<span className="font-semibold">{mmsMapData.allLines.length}</span> lines loaded</>
						)}
					</p>
				</div>
			)}

			{/* Create Model Modal */}
			<CreateModelModal
				isOpen={isCreateModalOpen}
				onClose={() => setIsCreateModalOpen(false)}
				onSuccess={(modelId, fileName) => {
					handleModelSelect(modelId, fileName);
					setTreeRefreshKey((prev) => prev + 1);
					setIsCreateModalOpen(false);
				}}
			/>
		</div>
	);
};

export default MmsSidePanel;
